import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Skeleton,
  Button,
} from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Close, BarChart } from '@mui/icons-material';
import { useGetCasesStatistics } from '@frontend/features/cases/queries';
import { useSearchParams } from 'react-router-dom';
import { parseLegacyDateFilters, parseDirectDateFilters } from '@frontend/features/cases/utils/dateFilters';

interface CaseStatisticsModalProps {
  open: boolean;
  onClose: () => void;
}

interface StatRow {
  id: string | number;
  name: string;
  count: number;
}

const StatisticsTable = ({ title, data, isLoading }: { 
  title: string; 
  data: StatRow[]; 
  isLoading: boolean; 
}) => {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>{title}</Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('common.name')}</TableCell>
                <TableCell align="right">{t('common.count')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[...Array(3)].map((_, index) => (
                <TableRow key={index}>
                  <TableCell><Skeleton variant="text" width={120} /></TableCell>
                  <TableCell align="right"><Skeleton variant="text" width={40} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>{title}</Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('common.name')}</TableCell>
              <TableCell align="right">{t('common.count')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={2} align="center">
                  <Typography variant="body2" color="text.secondary">
                    {t('common.noData')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              data.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.name}</TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="medium">
                      {row.count.toLocaleString()}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export const CaseStatisticsModal = ({ open, onClose }: CaseStatisticsModalProps) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const standardFilters = {
    status: searchParams.get('status')?.split('|') || [],
    teamIds: searchParams.get('teamIds')?.split('|').map((i) => 
      i === 'notselected' ? 'notselected' : Number(i)
    ) || [],
    workflowIds: searchParams.get('workflowIds')?.split('|') || [],
    stepIds: searchParams.get('stepIds')?.split('|') || [],
    clientIds: searchParams.get('clientIds')?.split('|') || [],
    agentIds: searchParams.get('agentIds')?.split('|') || [],
    originatedByChannels: searchParams.get('originatedByChannel')?.split('|') || [],
  } as any;

  // Procesar filtros de fecha para el modal de estadísticas
  const legacyDateFilters = parseLegacyDateFilters(searchParams);
  const directDateFilters = parseDirectDateFilters(searchParams);
  const dateFilters = { ...legacyDateFilters, ...directDateFilters };

  // Filtros para campos personalizados, excluyendo filtros de fecha procesados
  const dateFilterKeys = ['createdDateFilter', 'updatedDateFilter', 'createdCustomDays', 'updatedCustomDays', 
    'createdSpecificDate', 'updatedSpecificDate', 'createdDateFrom', 'createdDateTo', 'updatedDateFrom', 
    'updatedDateTo', 'createdAfter', 'createdBefore', 'updatedAfter', 'updatedBefore'];

  // Procesar custom date field filters para estadísticas
  const customDateFieldFilters: { fieldName: string; after?: string; before?: string }[] = [];
  const customDateFieldKeys = new Set<string>();
  
  // Buscar parámetros con patrón fieldId_fieldName_after y fieldId_fieldName_before
  for (const [key, value] of searchParams.entries()) {
    const afterMatch = key.match(/^(.+)_after$/);
    const beforeMatch = key.match(/^(.+)_before$/);
    
    if (afterMatch && value) {
      const fieldName = afterMatch[1];
      customDateFieldKeys.add(fieldName);
      let existingFilter = customDateFieldFilters.find(f => f.fieldName === fieldName);
      if (!existingFilter) {
        existingFilter = { fieldName };
        customDateFieldFilters.push(existingFilter);
      }
      existingFilter.after = value;
    } else if (beforeMatch && value) {
      const fieldName = beforeMatch[1];
      customDateFieldKeys.add(fieldName);
      let existingFilter = customDateFieldFilters.find(f => f.fieldName === fieldName);
      if (!existingFilter) {
        existingFilter = { fieldName };
        customDateFieldFilters.push(existingFilter);
      }
      existingFilter.before = value;
    }
  }
  
  // Agregar todas las claves de custom date fields a los filtros excluidos
  const customDateFieldFilterKeys = Array.from(customDateFieldKeys).flatMap(fieldName => [
    `${fieldName}_after`,
    `${fieldName}_before`,
    `${fieldName}_filter`,
    `${fieldName}_customDays`,
    `${fieldName}_specificDate`,
    `${fieldName}_dateFrom`,
    `${fieldName}_dateTo`
  ]);
  
  const allDateFilterKeys = [...dateFilterKeys, ...customDateFieldFilterKeys];

  const customFieldFilters = Array.from(searchParams.entries())
    .filter(
      ([key]) => !Object.keys(standardFilters).includes(key) && 
                 !allDateFilterKeys.includes(key) && 
                 key !== 'search' && key !== 'sort' && key !== 'order' && key !== 'page',
    )
    .map(([fieldName, value]) => ({ fieldName, value }));

  const { data: statsData, isLoading: statsLoading } = useGetCasesStatistics({
    ...standardFilters,
    ...dateFilters,
    customFieldFilters,
    customDateFieldFilters,
    currentPage: 1,
    sort: undefined,
    order: undefined,
    onErrorCallback: undefined,
    enabled: open,
  });

  const byTeam = statsData?.byTeam || [];
  const byStatus = statsData?.byStatus || [];
  const byWorkflow = statsData?.byWorkflow || [];
  const byAgent = statsData?.byAgent || [];
  const byAgency = statsData?.byAgency || [];
  const byChannel = statsData?.byChannel || [];
  const byStep = statsData?.byStep || [];

  const teamStatsData: StatRow[] = byTeam.map(stat => ({
    id: stat.teamId ?? t('common.na'),
    name: stat.teamName ?? t('common.na'),
    count: stat.count,
  }));

  const statusStatsData: StatRow[] = byStatus.map(stat => ({
    id: stat.status,
    name: t(`cases.status.${stat.status}`),
    count: stat.count
  }));

  const workflowStatsData: StatRow[] = byWorkflow.map(stat => ({
    id: stat.workflowId ?? t('common.na'),
    name: stat.workflowName ?? t('common.na'),
    count: stat.count
  }));

  const agentStatsData: StatRow[] = useMemo(() => {
    const groupedByName = byAgent.reduce((acc, stat) => {
      const name = stat.agentName === 'Sin agente asignado' || !stat.agentName 
        ? t('common.na') 
        : stat.agentName;
      
      if (acc[name]) {
        acc[name] += stat.count;
      } else {
        acc[name] = stat.count;
      }
      return acc;
    }, {} as Record<string, number>);
    
    return Object.entries(groupedByName).map(([name, count], index) => ({
      id: `agent-${index}`,
      name,
      count
    }));
  }, [byAgent, t]);

  const agencyStatsData: StatRow[] = byAgency.map(stat => ({
    id: stat.agencyId,
    name: stat.agencyName,
    count: stat.count
  }));

  const channelStatsData: StatRow[] = byChannel.map(stat => ({
    id: stat.channel ?? t('common.na'),
    name: stat.channel ? t(`cases.originatedByChannel.${stat.channel}`) : t('common.na'),
    count: stat.count
  }));

  const stepStatsData: StatRow[] = byStep.map(stat => ({
    id: stat.stepId ?? t('common.na'),
    name: stat.stepName ?? t('common.na'),
    count: stat.count
  }));

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          minHeight: '60vh'
        }
      }}
    >
      <DialogTitle sx={{ m: 0, p: 3, pb: 1 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <BarChart color="primary" />
          <Typography variant="h5" component="h2">
            {t('cases.statistics.title')}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Box display="flex" flexDirection="column" gap={3}>
          <StatisticsTable
            title={t('cases.statistics.byStatus')}
            data={statusStatsData}
            isLoading={statsLoading}
          />
          
          <StatisticsTable
            title={t('cases.statistics.byTeam')}
            data={teamStatsData}
            isLoading={statsLoading}
          />

          <StatisticsTable
            title={t('cases.statistics.byWorkflow')}
            data={workflowStatsData}
            isLoading={statsLoading}
          />

          <StatisticsTable
            title={t('cases.statistics.byAgent')}
            data={agentStatsData}
            isLoading={statsLoading}
          />

          <StatisticsTable
            title={t('cases.statistics.byAgency')}
            data={agencyStatsData}
            isLoading={statsLoading}
          />

          <StatisticsTable
            title={t('cases.statistics.byChannel')}
            data={channelStatsData}
            isLoading={statsLoading}
          />

          <StatisticsTable
            title={t('cases.statistics.byStep')}
            data={stepStatsData}
            isLoading={statsLoading}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="outlined">
          {t('common.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}; 